/**
 * Invoice Generator — client logic
 *
 * Single-input portal tool. Rep types a Shopify order name, hits Generate,
 * and a branded PDF for that order opens in a new browser tab.
 *
 * Backend: POST {WORKER_URL}/generate-order-invoice
 *   body: { orderInput: "SHOP-1234" }
 *   response: PDF binary (Content-Type: application/pdf) on 200,
 *             { error: "..." } JSON on non-200.
 *   auth:    Authorization: Bearer <accessToken from rep_portal_session>
 */
(function () {
  'use strict';

  var WORKER_URL = 'https://rfq-worker.ryan-29b.workers.dev';
  var ENDPOINT = '/generate-order-invoice';
  var LOGIN_PAGE = '/pages/sales-team-portal';

  // ------------------------------------------------------------------
  // DOM helpers
  // ------------------------------------------------------------------
  function qs(sel, root) { return (root || document).querySelector(sel); }

  function getElements() {
    return {
      form:        qs('[data-invgen-form]'),
      input:       qs('[data-invgen-input]'),
      btn:         qs('[data-invgen-submit]'),
      btnLabel:    qs('[data-invgen-btn-label]') || qs('[data-invgen-submit] .invgen-btn__label'),
      btnSpinner:  qs('[data-invgen-submit] .invgen-btn__spinner'),
      receipt:     qs('[data-invgen-receipt]'),
      status:      qs('[data-invgen-status]'),
      statusMsg:   qs('[data-invgen-status-msg]')
    };
  }

  // ------------------------------------------------------------------
  // Status display
  // ------------------------------------------------------------------
  function showStatus(els, kind, message) {
    if (!els.status || !els.statusMsg) return;
    els.status.classList.remove('invgen-status--success', 'invgen-status--error');
    if (kind) els.status.classList.add('invgen-status--' + kind);
    els.statusMsg.textContent = message;
    els.status.hidden = false;
  }

  function clearStatus(els) {
    if (els.status) els.status.hidden = true;
  }

  function setLoading(els, on) {
    if (!els.btn) return;
    els.btn.disabled = !!on;
    if (els.btnLabel) els.btnLabel.hidden = !!on;
    if (els.btnSpinner) els.btnSpinner.hidden = !on;
  }

  // ------------------------------------------------------------------
  // Auth — use shared portal helper for token + auto-refresh
  // ------------------------------------------------------------------
  async function getAccessToken() {
    if (window.RepPortal && window.RepPortal.Auth && typeof window.RepPortal.Auth.getAccessToken === 'function') {
      return window.RepPortal.Auth.getAccessToken();
    }
    // Fallback: pull from sessionStorage directly. Loses auto-refresh but
    // works if custom-rep-portal.js hasn't loaded yet.
    try {
      var raw = sessionStorage.getItem('rep_portal_session') || localStorage.getItem('rep_portal_session');
      var session = raw ? JSON.parse(raw) : null;
      return session && session.accessToken ? session.accessToken : null;
    } catch (e) {
      return null;
    }
  }

  // ------------------------------------------------------------------
  // Submit handler
  // ------------------------------------------------------------------
  async function handleSubmit(els, e) {
    e.preventDefault();
    clearStatus(els);

    var raw = (els.input.value || '').trim();
    if (!raw) {
      showStatus(els, 'error', 'Enter an order number.');
      els.input.focus();
      return;
    }

    var isReceipt = !!(els.receipt && els.receipt.checked);
    setLoading(els, true);

    try {
      var token = await getAccessToken();
      if (!token) {
        window.location.href = LOGIN_PAGE;
        return;
      }

      var resp = await fetch(WORKER_URL + ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({ orderInput: raw, mode: isReceipt ? 'receipt' : 'invoice' })
      });

      if (resp.status === 401) {
        // Session expired — clear and bounce to portal login
        try { if (window.RepPortal && window.RepPortal.Auth) window.RepPortal.Auth.clearSession(); } catch (_) {}
        window.location.href = LOGIN_PAGE;
        return;
      }

      if (!resp.ok) {
        var errData = null;
        try { errData = await resp.json(); } catch (_) { errData = null; }
        var errMsg = (errData && errData.error) || ('Request failed (' + resp.status + ')');
        showStatus(els, 'error', errMsg);
        return;
      }

      // Worker now uploads the PDF to R2 and returns a JSON response with the URL.
      // The /files/{key} route serves with Content-Disposition: inline + the
      // friendlyName, so the browser previews the PDF and the rich filename is
      // preserved when the user saves from the preview.
      var data = null;
      try { data = await resp.json(); } catch (_) { data = null; }
      if (!data || !data.success || !data.pdfUrl) {
        showStatus(els, 'error', (data && data.error) || 'Failed to generate invoice.');
        return;
      }

      var win = window.open(data.pdfUrl, '_blank');
      if (!win) {
        showStatus(els, 'error', 'Popup blocked. Allow popups for this site and try again.');
        return;
      }

      var docNoun = isReceipt ? 'Receipt' : 'Invoice';
      showStatus(els, 'success', docNoun + ' opened in a new tab — "' + (data.filename || docNoun + '.pdf') + '"');
    } catch (err) {
      var msg = (err && err.message) || 'Network error — check your connection and try again.';
      showStatus(els, 'error', msg);
    } finally {
      setLoading(els, false);
    }
  }

  // ------------------------------------------------------------------
  // Init
  // ------------------------------------------------------------------
  function init() {
    var els = getElements();
    if (!els.form || !els.input || !els.btn) return; // not on this page

    els.form.addEventListener('submit', function (e) { handleSubmit(els, e); });

    // Clear stale status when the user starts editing again.
    els.input.addEventListener('input', function () { clearStatus(els); });

    // Toggle button label between Invoice and Receipt
    if (els.receipt && els.btnLabel) {
      var updateBtnLabel = function () {
        els.btnLabel.textContent = els.receipt.checked ? 'Generate Receipt' : 'Generate Invoice';
      };
      els.receipt.addEventListener('change', function () { updateBtnLabel(); clearStatus(els); });
      updateBtnLabel();
    }

    // Autofocus the input when the page loads (after auth gate clears).
    setTimeout(function () {
      try { els.input.focus({ preventScroll: true }); } catch (_) { els.input.focus(); }
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
